import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { toolboxService } from '../services/api';

const HKSH_REPORT_PRESET_CLIDS = [
  { id: 'HKSH0002', label: 'HKSH0002 (Ophthalmology — Happy Valley)' },
  { id: 'HKSH0003', label: 'HKSH0003 (Ophthalmology — Admiralty)' },
  { id: 'HKSH0005', label: 'HKSH0005 (Obstetrics & Gynaecology)' },
  { id: 'HKSH0006', label: 'HKSH0006 (Dermatology Centre)' },
];

const PRESET_IDS = HKSH_REPORT_PRESET_CLIDS.map((c) => c.id);

const PREVIEW_TAB_OBSERVE = 'observe';
const PREVIEW_TAB_RESULT = 'result';

function PreviewTable({ title, rows, total, note, wrapperClassName = 'mt-6', hideTitle = false }) {
  const columns = useMemo(() => {
    if (!rows || rows.length === 0) return [];
    return Object.keys(rows[0]);
  }, [rows]);

  if (!rows || rows.length === 0) {
    return (
      <div className={wrapperClassName}>
        {hideTitle ? null : <h2 className="text-lg font-semibold text-gray-900 mb-2">{title}</h2>}
        <p className="text-sm text-gray-500">No rows.</p>
      </div>
    );
  }

  return (
      <div className={`${wrapperClassName} overflow-x-auto`}>
      <div
        className={`flex flex-wrap items-baseline gap-2 mb-2 ${
          hideTitle ? 'justify-end' : 'justify-between'
        }`}
      >
        {hideTitle ? (
          <span className="sr-only">{title}</span>
        ) : (
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        )}
        <p className="text-sm text-gray-500">
          Showing {rows.length} of {total}
          {note ? ` — ${note}` : ''}
        </p>
      </div>
      <table className="min-w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
        <thead className="bg-gray-50">
          <tr>
            {columns.map((col) => (
              <th
                key={col}
                className="px-3 py-2 text-left font-medium text-gray-600 border-b border-gray-200 whitespace-nowrap"
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/80'}>
              {columns.map((col) => (
                <td
                  key={col}
                  className="px-3 py-2 border-b border-gray-100 text-gray-800 max-w-xs truncate"
                  title={row[col] != null ? String(row[col]) : ''}
                >
                  {row[col] == null ? '' : String(row[col])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const MODE_MONTH = 'month';
const MODE_RANGE = 'range';

const HKSHReportGenerator = () => {
  const [mode, setMode] = useState(MODE_MONTH);
  const [yearMonth, setYearMonth] = useState(dayjs().format('YYYY-MM'));
  const [startDate, setStartDate] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(dayjs().add(1, 'month').startOf('month').format('YYYY-MM-DD'));
  const [presetOn, setPresetOn] = useState(() =>
    Object.fromEntries(PRESET_IDS.map((id) => [id, true]))
  );
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewTableTab, setPreviewTableTab] = useState(PREVIEW_TAB_OBSERVE);

  const selectedClids = useMemo(
    () => PRESET_IDS.filter((id) => presetOn[id]),
    [presetOn]
  );

  const isRangeValid = startDate && endDate && startDate < endDate;
  const canRun =
    selectedClids.length > 0 &&
    (mode === MODE_MONTH ? Boolean(yearMonth) : isRangeValid);

  const togglePreset = (clid) => {
    setPresetOn((prev) => ({ ...prev, [clid]: !prev[clid] }));
  };

  const handlePreview = async () => {
    setLoading(true);
    setError('');
    setPreview(null);
    const res = await toolboxService.previewHKSHReport({
      mode,
      yearMonth,
      startDate,
      endDate,
      previewLimit: 500,
      clids: selectedClids,
    });
    setLoading(false);
    if (!res.success) {
      setError(res.error || 'Preview failed');
      return;
    }
    setPreview(res.data);
    setPreviewTableTab(PREVIEW_TAB_OBSERVE);
  };

  const handleExport = async () => {
    setExporting(true);
    setError('');
    const res = await toolboxService.exportHKSHReportZip({
      mode,
      yearMonth,
      startDate,
      endDate,
      clids: selectedClids,
    });
    setExporting(false);
    if (!res.success) {
      setError(res.error || 'Export failed');
      return;
    }
    const tag =
      mode === MODE_RANGE
        ? `${startDate.replace(/-/g, '')}_${endDate.replace(/-/g, '')}`
        : yearMonth.replace(/-/g, '');
    const url = URL.createObjectURL(res.blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `hksh_report_${tag}.zip`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-4">
        <Link to="/toolbox" className="text-sm text-[#075e54] hover:underline">
          Back to Tool Box
        </Link>
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">HKSH Report Generator</h1>
        <p className="text-sm text-gray-600 mb-6">
          Choose a date range and which preset CLIDs to include, then run preview or download the four-CSV ZIP.
        </p>

        <fieldset className="mb-4 border border-gray-200 rounded-md p-3">
          <legend className="text-xs font-medium text-gray-500 px-1">Preset CLIDs</legend>
          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 mt-1">
            {HKSH_REPORT_PRESET_CLIDS.map(({ id, label }) => (
              <label key={id} className="inline-flex items-center gap-2 text-sm text-gray-800 cursor-pointer">
                <input
                  type="checkbox"
                  checked={Boolean(presetOn[id])}
                  onChange={() => togglePreset(id)}
                  className="rounded border-gray-300"
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
        </fieldset>

        <div className="mb-4 flex gap-4">
          <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="radio"
              name="hksh-mode"
              value={MODE_MONTH}
              checked={mode === MODE_MONTH}
              onChange={() => setMode(MODE_MONTH)}
            />
            By month
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="radio"
              name="hksh-mode"
              value={MODE_RANGE}
              checked={mode === MODE_RANGE}
              onChange={() => setMode(MODE_RANGE)}
            />
            Custom date range
          </label>
        </div>

        <div className="flex flex-wrap items-end gap-4">
          {mode === MODE_MONTH ? (
            <div>
              <label htmlFor="hksh-month" className="block text-xs font-medium text-gray-500 mb-1">
                Month
              </label>
              <input
                id="hksh-month"
                type="month"
                value={yearMonth}
                onChange={(e) => setYearMonth(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
            </div>
          ) : (
            <>
              <div>
                <label htmlFor="hksh-start-date" className="block text-xs font-medium text-gray-500 mb-1">
                  Start date (inclusive)
                </label>
                <input
                  id="hksh-start-date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label htmlFor="hksh-end-date" className="block text-xs font-medium text-gray-500 mb-1">
                  End date (exclusive)
                </label>
                <input
                  id="hksh-end-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
              {startDate && endDate && !isRangeValid && (
                <p className="text-xs text-red-500 self-end pb-2">End date must be after start date.</p>
              )}
            </>
          )}
          <button
            type="button"
            onClick={handlePreview}
            disabled={loading || !canRun}
            className="px-4 py-2 rounded-md bg-[#075e54] text-white text-sm font-medium disabled:opacity-50"
          >
            {loading ? 'Loading…' : 'Run preview'}
          </button>
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting || !canRun}
            className="px-4 py-2 rounded-md border border-gray-300 text-sm font-medium text-gray-800 disabled:opacity-50"
          >
            {exporting ? 'Preparing ZIP…' : 'Download ZIP (4 CSVs)'}
          </button>
        </div>

        {error ? (
          <p className="mt-4 text-sm text-red-600" role="alert">
            {error}
          </p>
        ) : null}

        {preview && (
          <div className="mt-6">
            <p className="text-sm text-gray-700">
              <span className="font-medium">CLIDs:</span>{' '}
              {(preview.clids || []).join(', ') || '—'}
            </p>
            <p className="text-sm text-gray-700 mt-1">
              <span className="font-medium">Range:</span> {preview.start_ts} → {preview.end_ts}
            </p>
            <dl className="mt-2 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 text-sm">
              <div>
                <dt className="text-gray-500">Comment rows</dt>
                <dd className="font-medium">{preview.counts?.flow_comment_rows}</dd>
              </div>
              <div>
                <dt className="text-gray-500">NPS scoring rows</dt>
                <dd className="font-medium">{preview.counts?.nps_scoring_rows}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Result rows</dt>
                <dd className="font-medium">{preview.counts?.result_rows}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Observe rows</dt>
                <dd className="font-medium">{preview.counts?.observe_rows}</dd>
              </div>
              <div className="col-span-2 sm:col-span-1 lg:col-span-1">
                <dt className="text-gray-500">Average NPS score</dt>
                <dd className="font-medium">
                  {preview.average_nps_score != null && preview.average_nps_score !== undefined
                    ? Number(preview.average_nps_score).toFixed(2)
                    : '—'}
                </dd>
                {preview.nps_score_sum != null &&
                preview.nps_score_sum !== undefined &&
                preview.counts?.result_rows != null ? (
                  <dd className="text-xs text-gray-500 font-normal mt-0.5">
                    Σ {Number(preview.nps_score_sum).toFixed(2)} ÷ {preview.counts.result_rows} result rows
                  </dd>
                ) : null}
              </div>
            </dl>
            <p className="mt-2 text-xs text-gray-500">
              Merge: unmatched comment rows {preview.counts?.merge_unmatched_comment}, single match{' '}
              {preview.counts?.merge_single_scoring_row}, multi scoring {preview.counts?.merge_multi_scoring_row}
            </p>
          </div>
        )}

        {preview && (
          <div className="mt-6">
            <div className="border-b border-gray-200">
              <nav className="flex flex-wrap gap-1" role="tablist" aria-label="Preview tables">
                <button
                  type="button"
                  role="tab"
                  id="hksh-tab-observe"
                  aria-selected={previewTableTab === PREVIEW_TAB_OBSERVE}
                  aria-controls="hksh-panel-observe"
                  onClick={() => setPreviewTableTab(PREVIEW_TAB_OBSERVE)}
                  className={`px-4 py-2 text-sm font-medium rounded-t-md border border-b-0 transition-colors ${
                    previewTableTab === PREVIEW_TAB_OBSERVE
                      ? 'bg-white border-gray-200 text-[#075e54] -mb-px z-[1] relative'
                      : 'bg-gray-50 border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  Observe (conversations with comments)
                </button>
                <button
                  type="button"
                  role="tab"
                  id="hksh-tab-result"
                  aria-selected={previewTableTab === PREVIEW_TAB_RESULT}
                  aria-controls="hksh-panel-result"
                  onClick={() => setPreviewTableTab(PREVIEW_TAB_RESULT)}
                  className={`px-4 py-2 text-sm font-medium rounded-t-md border border-b-0 transition-colors ${
                    previewTableTab === PREVIEW_TAB_RESULT
                      ? 'bg-white border-gray-200 text-[#075e54] -mb-px z-[1] relative'
                      : 'bg-gray-50 border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  Result (merged)
                </button>
              </nav>
            </div>
            <div
              className="border border-t-0 border-gray-200 rounded-b-lg rounded-tr-lg p-4 bg-white"
              role="tabpanel"
              id={
                previewTableTab === PREVIEW_TAB_OBSERVE ? 'hksh-panel-observe' : 'hksh-panel-result'
              }
              aria-labelledby={
                previewTableTab === PREVIEW_TAB_OBSERVE ? 'hksh-tab-observe' : 'hksh-tab-result'
              }
            >
              {previewTableTab === PREVIEW_TAB_OBSERVE ? (
                <PreviewTable
                  title="Observe (conversations with comments)"
                  rows={preview.observe_preview}
                  total={preview.observe_total}
                  note="truncated to 500 rows in preview"
                  wrapperClassName="mt-0"
                  hideTitle
                />
              ) : (
                <PreviewTable
                  title="Result (merged)"
                  rows={preview.result_preview}
                  total={preview.result_total}
                  note="truncated to 500 rows in preview"
                  wrapperClassName="mt-0"
                  hideTitle
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HKSHReportGenerator;
